import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Grid, Chip, IconButton,
  Divider, Tooltip, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LockIcon from '@mui/icons-material/Lock';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API = '/api/contabilidad';
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtSigned(v) {
  const n = parseFloat(v || 0);
  return (n >= 0 ? '+' : '') + fmt(n);
}

export default function ConciliacionBancaria() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [conciliaciones, setConciliaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Detalle de una conciliación abierta
  const [conciliacionActiva, setConciliacionActiva] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Dialog nueva conciliación
  const [dlgNueva, setDlgNueva] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [formNueva, setFormNueva] = useState({ cuenta_id: '', periodo: '', saldo_inicial: '', saldo_final_extracto: '' });
  const [saving, setSaving] = useState(false);

  // Importar extracto
  const [dlgExtracto, setDlgExtracto] = useState(false);
  const [fileExtracto, setFileExtracto] = useState(null);
  const [uploadingExtracto, setUploadingExtracto] = useState(false);

  // Conciliar manual: extracto seleccionado + movdet seleccionado
  const [extractoSel, setExtractoSel] = useState(null);
  const [movdetSel, setMovdetSel] = useState(null);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    cargarConciliaciones();
    axios.get(`${API}/plan?empresa_id=${empresaId}`).then(r => {
      setCuentas(r.data.filter(c => /^1[0-9]/.test(c.codigo) && c.nivel >= 2));
    }).catch(() => {});
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      setPeriodos(r.data);
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a,b) => b.periodo.localeCompare(a.periodo));
      if (abiertos.length > 0) setFormNueva(f => ({ ...f, periodo: abiertos[0].periodo }));
    }).catch(() => {});
  }, [empresaId]);

  const cargarConciliaciones = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/conciliacion?empresa_id=${empresaId}`);
      setConciliaciones(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    setLoading(false);
  }, [empresaId]);

  const abrirDetalle = async (conc) => {
    setConciliacionActiva(conc);
    setLoadingDetalle(true);
    setExtractoSel(null);
    setMovdetSel(null);
    try {
      const r = await axios.get(`${API}/conciliacion/${conc.id}`);
      setDetalle(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar detalle'); }
    setLoadingDetalle(false);
  };

  const recargarDetalle = async () => {
    if (!conciliacionActiva) return;
    setLoadingDetalle(true);
    try {
      const r = await axios.get(`${API}/conciliacion/${conciliacionActiva.id}`);
      setDetalle(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoadingDetalle(false);
  };

  async function crearConciliacion() {
    if (!formNueva.cuenta_id || !formNueva.periodo) { setError('Cuenta y período son requeridos'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/conciliacion`, { ...formNueva, empresa_id: empresaId });
      setSuccess('Conciliación creada');
      setDlgNueva(false);
      cargarConciliaciones();
      const conc = await axios.get(`${API}/conciliacion/${r.data.id}`);
      setConciliacionActiva({ id: r.data.id, ...formNueva });
      setDetalle(conc.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al crear'); }
    setSaving(false);
  }

  async function importarExtracto() {
    if (!fileExtracto) { setError('Selecciona un archivo'); return; }
    setUploadingExtracto(true);
    try {
      const fd = new FormData();
      fd.append('archivo', fileExtracto);
      fd.append('conciliacion_id', conciliacionActiva.id);
      const r = await axios.post(`${API}/conciliacion/importar-extracto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(`${r.data.importados} líneas importadas del extracto`);
      setDlgExtracto(false);
      setFileExtracto(null);
      recargarDetalle();
    } catch (e) { setError(e.response?.data?.error || 'Error al importar'); }
    setUploadingExtracto(false);
  }

  async function conciliarManual() {
    if (!extractoSel || !movdetSel) { setError('Selecciona una línea del extracto y un movimiento del libro'); return; }
    try {
      await axios.post(`${API}/conciliacion/conciliar`, { extracto_id: extractoSel.id, movdet_id: movdetSel.movdet_id });
      setSuccess('Líneas conciliadas correctamente');
      setExtractoSel(null); setMovdetSel(null);
      recargarDetalle();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function desconciliar(extractoId) {
    try {
      await axios.post(`${API}/conciliacion/desconciliar`, { extracto_id: extractoId });
      setSuccess('Desconciliado');
      recargarDetalle();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function conciliarAuto() {
    if (!window.confirm('¿Ejecutar conciliación automática? Se emparejará por monto exacto.')) return;
    try {
      const r = await axios.post(`${API}/conciliacion/conciliar-automatico`, { conciliacion_id: conciliacionActiva.id });
      setSuccess(`Conciliación automática: ${r.data.conciliados} líneas conciliadas`);
      recargarDetalle();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function cerrarConciliacion() {
    if (!window.confirm('¿Cerrar esta conciliación? No se podrán agregar más movimientos.')) return;
    try {
      const r = await axios.put(`${API}/conciliacion/${conciliacionActiva.id}/cerrar`);
      setSuccess(r.data.con_diferencias ? 'Cerrada con diferencias' : 'Conciliación cerrada correctamente');
      recargarDetalle();
      cargarConciliaciones();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  const estadoColor = (estado) => {
    if (estado === 'ABIERTA') return 'primary';
    if (estado === 'CERRADA') return 'success';
    return 'warning';
  };

  const pctConciliado = detalle ? Math.round(
    ((detalle.stats.extracto_conciliado) / Math.max(detalle.stats.extracto_total, 1)) * 100
  ) : 0;

  // ── DETALLE VIEW ──
  if (conciliacionActiva && detalle) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => { setConciliacionActiva(null); setDetalle(null); }}><ArrowBackIcon /></IconButton>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {detalle.cuenta_codigo} — {detalle.cuenta_nombre} | Período {detalle.periodo}
          </Typography>
          <Chip label={detalle.estado} color={estadoColor(detalle.estado)} size="small" sx={{ ml: 1 }} />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Stats */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Saldo inicial</Typography>
              <Typography fontWeight="bold">S/ {fmt(detalle.saldo_inicial)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Saldo extracto banco</Typography>
              <Typography fontWeight="bold">S/ {fmt(detalle.saldo_final_extracto)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary">Extracto conciliado</Typography>
              <Typography fontWeight="bold" color={detalle.stats.extracto_conciliado === detalle.stats.extracto_total && detalle.stats.extracto_total > 0 ? 'success.main' : 'inherit'}>
                {detalle.stats.extracto_conciliado} / {detalle.stats.extracto_total} líneas
              </Typography>
              <LinearProgress variant="determinate" value={pctConciliado} sx={{ mt: 0.5 }} color={pctConciliado === 100 ? 'success' : 'primary'} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {detalle.estado === 'ABIERTA' && (<>
                  <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setDlgExtracto(true)}>Importar Extracto</Button>
                  <Button size="small" variant="outlined" color="secondary" startIcon={<AutoFixHighIcon />} onClick={conciliarAuto}>Auto</Button>
                  <Button size="small" variant="contained" color="error" startIcon={<LockIcon />} onClick={cerrarConciliacion}>Cerrar</Button>
                </>)}
                <IconButton size="small" onClick={recargarDetalle}><RefreshIcon /></IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Botón conciliar manual */}
        {detalle.estado === 'ABIERTA' && extractoSel && movdetSel && (
          <Alert severity="info" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" startIcon={<LinkIcon />} onClick={conciliarManual}>Conciliar</Button>
          }>
            Extracto: {fmt(extractoSel.importe)} ({extractoSel.descripcion?.substring(0,40)}) ↔ Libro: {fmt(movdetSel.debe_mn || 0)} D / {fmt(movdetSel.haber_mn || 0)} H ({movdetSel.glosa_det?.substring(0,40)})
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Extracto banco */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Extracto Bancario</Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Acc.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detalle.extracto || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin líneas — importa el extracto bancario</TableCell></TableRow>
                  ) : (detalle.extracto || []).map(e => {
                    const isSel = extractoSel?.id === e.id;
                    return (
                      <TableRow key={e.id} hover
                        selected={isSel}
                        onClick={() => detalle.estado === 'ABIERTA' && e.estado === 'PENDIENTE' && setExtractoSel(isSel ? null : e)}
                        sx={{ cursor: detalle.estado === 'ABIERTA' && e.estado === 'PENDIENTE' ? 'pointer' : 'default', bgcolor: isSel ? 'action.selected' : undefined }}>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{e.fecha}</TableCell>
                        <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{e.descripcion}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: parseFloat(e.importe) >= 0 ? 'success.main' : 'error.main', fontSize: '0.75rem' }}>
                          {fmtSigned(e.importe)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={e.estado} size="small" color={e.estado === 'CONCILIADO' ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="center">
                          {e.estado === 'CONCILIADO' && detalle.estado === 'ABIERTA' && (
                            <Tooltip title="Desconciliar"><IconButton size="small" onClick={ev => { ev.stopPropagation(); desconciliar(e.id); }}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Libros contables */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Movimientos en Libros</Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Glosa</TableCell>
                    <TableCell align="right">Debe</TableCell>
                    <TableCell align="right">Haber</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detalle.libros || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No hay movimientos en libros para este período y cuenta</TableCell></TableRow>
                  ) : (detalle.libros || []).map(l => {
                    const isSel = movdetSel?.movdet_id === l.movdet_id;
                    return (
                      <TableRow key={l.movdet_id} hover
                        selected={isSel}
                        onClick={() => detalle.estado === 'ABIERTA' && !l.conciliado && setMovdetSel(isSel ? null : l)}
                        sx={{ cursor: detalle.estado === 'ABIERTA' && !l.conciliado ? 'pointer' : 'default', bgcolor: isSel ? 'action.selected' : undefined }}>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{l.fecha}</TableCell>
                        <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{l.glosa_det || l.glosa}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{parseFloat(l.debe_mn) > 0 ? fmt(l.debe_mn) : ''}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{parseFloat(l.haber_mn) > 0 ? fmt(l.haber_mn) : ''}</TableCell>
                        <TableCell align="center">
                          <Chip label={l.conciliado ? 'Conciliado' : 'Pendiente'} size="small" color={l.conciliado ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {/* Dialog importar extracto */}
        <Dialog open={dlgExtracto} onClose={() => setDlgExtracto(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Importar Extracto Bancario</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Formatos soportados: BCP, Interbank, Scotiabank, BBVA (Excel .xlsx)<br />
              Columnas esperadas: Fecha | Descripción | Débito | Crédito (o Fecha | Descripción | Tipo | Importe)
            </Alert>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} fullWidth>
              {fileExtracto ? fileExtracto.name : 'Seleccionar archivo Excel (.xlsx)'}
              <input type="file" accept=".xlsx,.xls" hidden onChange={e => setFileExtracto(e.target.files?.[0] || null)} />
            </Button>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDlgExtracto(false)}>Cancelar</Button>
            <Button variant="contained" onClick={importarExtracto} disabled={!fileExtracto || uploadingExtracto}>
              {uploadingExtracto ? <CircularProgress size={20} color="inherit" /> : 'Importar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── LISTA VIEW ──
  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <AccountBalanceIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Conciliación Bancaria</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlgNueva(true)} disabled={!empresaId}>Nueva Conciliación</Button>
              <IconButton onClick={cargarConciliaciones} disabled={!empresaId || loading}><RefreshIcon /></IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cuenta</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Período</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Saldo Inicial</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Saldo Extracto</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Estado</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Fecha Cierre</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Abrir</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conciliaciones.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay conciliaciones — crea una nueva</TableCell></TableRow>
              ) : conciliaciones.map(c => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{c.cuenta_codigo} {c.cuenta_nombre}</TableCell>
                  <TableCell>{c.periodo}</TableCell>
                  <TableCell align="right">S/ {fmt(c.saldo_inicial)}</TableCell>
                  <TableCell align="right">S/ {fmt(c.saldo_final_extracto)}</TableCell>
                  <TableCell align="center">
                    <Chip label={c.estado} size="small" color={estadoColor(c.estado)} />
                  </TableCell>
                  <TableCell align="center">{c.fecha_cierre || '—'}</TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="outlined" onClick={() => abrirDetalle(c)}>Abrir</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog nueva conciliación */}
      <Dialog open={dlgNueva} onClose={() => setDlgNueva(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Conciliación Bancaria</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Cuenta Bancaria" value={formNueva.cuenta_id}
                onChange={e => setFormNueva(f => ({ ...f, cuenta_id: e.target.value }))}>
                {cuentas.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Período" value={formNueva.periodo}
                onChange={e => setFormNueva(f => ({ ...f, periodo: e.target.value }))}>
                {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Saldo Inicial (S/)" type="number" value={formNueva.saldo_inicial}
                onChange={e => setFormNueva(f => ({ ...f, saldo_inicial: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Saldo Final Extracto (S/)" type="number" value={formNueva.saldo_final_extracto}
                onChange={e => setFormNueva(f => ({ ...f, saldo_final_extracto: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgNueva(false)}>Cancelar</Button>
          <Button variant="contained" onClick={crearConciliacion} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

